import React from 'react'
import { AxiosError } from 'axios'
import { ordersApi } from 'api/orders'
import { EOrderStatus, IAdminMultiOrderResponse, IOrderTimeline } from 'models/orders'
import { showServerError } from 'shared/utils/showServerError'

const MISSING_STATUSES = [400, 404, 500]

interface IUseSuperOrder {
  superOrder: IAdminMultiOrderResponse | null
  isLoading: boolean
  isMissing: boolean
  applyStatus: (
    orderId: string,
    status: EOrderStatus,
    timeline: IOrderTimeline
  ) => void
  reload: () => void
}

// orderId — идентификатор любого заказа из супер-заказа, по нему бэкенд отдаёт всю группу
export const useSuperOrder = (orderId?: string | null): IUseSuperOrder => {
  const [superOrder, setSuperOrder] =
    React.useState<IAdminMultiOrderResponse | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isMissing, setIsMissing] = React.useState(false)
  const [reloadKey, setReloadKey] = React.useState(0)

  React.useEffect(() => {
    if (!orderId) {
      return
    }

    // Ответ по прошлому заказу не должен перезаписать данные текущего
    let isActual = true

    const getSuperOrder = async () => {
      try {
        setIsLoading(true)
        setIsMissing(false)
        const response = await ordersApi.getSuperOrder(orderId)
        if (isActual) {
          setSuperOrder(response.data)
        }
      } catch (e) {
        const status = (e as AxiosError).response?.status
        if (status && MISSING_STATUSES.includes(status)) {
          if (isActual) {
            setIsMissing(true)
          }
          return
        }
        showServerError(e)
      } finally {
        if (isActual) {
          setIsLoading(false)
        }
      }
    }

    getSuperOrder()

    return () => {
      isActual = false
    }
  }, [orderId, reloadKey])

  const applyStatus = (
    changedOrderId: string,
    status: EOrderStatus,
    timeline: IOrderTimeline
  ) => {
    setSuperOrder((prev) => {
      if (!prev) {
        return prev
      }
      return {
        ...prev,
        orders: prev.orders.map((el) =>
          el._id === changedOrderId ? { ...el, status, ...timeline } : el
        )
      }
    })
  }

  const reload = () => setReloadKey((key) => key + 1)

  return { superOrder, isLoading, isMissing, applyStatus, reload }
}
